'use client';

import { EXCLUSIVE_LAUNCH_ZONE_DEFAULTS, SESSION_KEYS } from '@/utils/constants';
import type { FC } from 'react';
import { useEffect } from 'react';
import { remove, retrieve, store } from '@/utils/storage';
import { GameUrl } from '@/app/components/game/subcomponents/game-url/game-url';
import { scrollToStoredElement } from '@/utils/scroll/scroll';
import type { LayoutSlice } from '@/types/zones/zone';
import type { GameDetailsProps } from '@/types/zones/exclusive-zone';
import { classes } from '@/utils/classes/classes';
import { useGlobalContext } from '@/store/contexts/global-context';
import { PRODUCT_THROTTLES } from '@/utils/feature-flags/constants';
import { useLauncherUrlResolution } from '@/hooks/use-launcher-url-resolution';
import { RichTextParagraph } from '@/app/components/rich-text/subcomponents/paragraph';
import type { RichTextElement } from '@/app/components/rich-text/rich-text.types';

import './styles/@theme';

type ExclusiveGameDetailsProps = {
    layoutContent: LayoutSlice;
    gameDetailsProps: GameDetailsProps;
    isLandscape: boolean;
};

export const ExclusivePostLaunchGameDetails: FC<ExclusiveGameDetailsProps> = ({
    layoutContent,
    gameDetailsProps,
    isLandscape
}) => {
    const content = { ...layoutContent.primary, ...gameDetailsProps };
    const orientationClass = isLandscape ? 'landscape' : 'portrait';
    const { featureFlags } = useGlobalContext();
    const launcherResolution = useLauncherUrlResolution();
    const isEarlyAccess = featureFlags[PRODUCT_THROTTLES.HAS_EARLY_ACCESS];
    const showLogoInPortrait = !isEarlyAccess;

    useEffect(() => {
        const elementId = retrieve(SESSION_KEYS.SCROLL_TO) as string;
        scrollToStoredElement({ id: elementId, offset: 200 });
        remove(SESSION_KEYS.SCROLL_TO);
    }, []);

    let descriptionText;

    if (content.description?.[0]?.spans?.length && content.description?.[0]?.text) {
        descriptionText = content.description[0];
    }

    const logoExclusiveUrl = content.logo?.exclusive?.url;
    const announcementBackgroundUrl = content.announcement_background?.url;
    const logoUrl = content.logo?.url;
    const ctaButtonText = content.cta_button_text;
    const { launchParameters } = content;
    //TODO implement this after throttle is done
    // const promoThrottle = useSelector((state:RootState) => state?.pageConfig?.product?.data?.features?.some(
    //     (item: any) => item?.feature_name === PRODUCT_THROTTLES.HAS_PROMO_BUTTON
    // ));

    const isPromoAvailable = content?.promo_cta_button_title && content?.promo_url; /*&& promoThrottle*/
    const playNowButtonText = ctaButtonText || EXCLUSIVE_LAUNCH_ZONE_DEFAULTS.PLAY_NOW_BUTTON;

    const handlePromoClick = () => {
        store({ key: SESSION_KEYS.SCROLL_TO, value: 'exclusive-post-launch' }, 'session');
    };

    return (
        <div className={classes('exclusive-post-launch-game-details', orientationClass)}>
            <div className="wrapper">
                {logoExclusiveUrl && (isLandscape || showLogoInPortrait) && (
                    <img className="logo-exclusive-image" alt="Exclusive Game" src={logoExclusiveUrl} />
                )}

                {isLandscape && (
                    <>
                        {isEarlyAccess ? (
                            <>
                                {logoUrl && <img className="logo-image" alt="Exclusive Game Logo" src={logoUrl} />}
                                {announcementBackgroundUrl && (
                                    <img
                                        className="announcement-image"
                                        alt="Announcement Banner"
                                        src={announcementBackgroundUrl}
                                    />
                                )}
                            </>
                        ) : (
                            <>
                                {announcementBackgroundUrl && (
                                    <img
                                        className="announcement-image"
                                        alt="Announcement Banner"
                                        src={announcementBackgroundUrl}
                                    />
                                )}
                                {logoUrl && <img className="logo-image" alt="Exclusive Game Logo" src={logoUrl} />}
                            </>
                        )}
                        <div className="cta-buttons-wrapper" data-test-id="exclusive-post-launch-buttons">
                            <GameUrl launcherUrlProps={launchParameters}>
                                <div className={'button play-now-button'}>{playNowButtonText}</div>
                            </GameUrl>

                            {!!isPromoAvailable && (
                                <button
                                    id="promo-button"
                                    className="button promo-available-button"
                                    onClick={handlePromoClick}>
                                    {content.promo_cta_button_title}
                                </button>
                            )}
                        </div>
                        {descriptionText && (
                            <RichTextParagraph {...(descriptionText as RichTextElement)} className="description" />
                        )}
                    </>
                )}
            </div>
        </div>
    );
};
